//@flow
import * as React from 'react';
import { translate } from 'react-i18next';

import { BreakTypesEnum } from 'common/enums/BreakTypes';
import _ from 'lodash';
import { BreakShift } from 'common/models/internal/BreakShift';
import { AddressLatLng } from 'common/models/internal/AddressLatLng';
import { themr } from 'react-css-themr';
import theme from './BreakShiftDialog.scss';

import breaksDialogFactory from 'common/components/common/breaks/BreaksDialogFactory';
import BreakShiftTabs from 'common/entity/components/dayShift/BreakShiftTabs';
import type { TFunction } from 'react-i18next';
import DateTimeUtils, {
    CustomDateFormats,
    DateInputFormats
} from 'common/utils/DateTimeUtils';

const BreaksDialog = breaksDialogFactory({ BreakTabs: BreakShiftTabs });

type Props = {
    defaultBreaks: Array<Object>,
    breakSlotFor: string,
    closeBreaksDialog: () => void,
    onChange: ({ breaks: Array<Object>, slotFor: string }) => void,
    t: TFunction
};
class BreakShiftDialog extends React.PureComponent<Props> {
    getNewBreak = ({ breakType }) => {
        const duration = this.getTransactionDuration(breakType);
        const startShiftInstant = this.getStartShiftInstant(breakType);
        const endShiftInstant = this.getEndShiftInstant(breakType);
        const location = null;
        return BreakShift.fromJS({
            duration,
            startShiftInstant,
            endShiftInstant,
            location,
            breakType
        });
    };

    getStartShiftInstant = breakType => {
        let time = '12:00 PM';
        const day = 1;
        if (breakType === BreakTypesEnum.Night) {
            time = '10:00 PM';
        }
        return {
            time: DateTimeUtils.customFormat(
                time,
                CustomDateFormats.API_TIME,
                DateInputFormats.HOUR12_SHORT
            ),
            day
        };
    };

    getEndShiftInstant = breakType => {
        let time = '02:00 PM';
        let day = 1;
        if (breakType === BreakTypesEnum.Night) {
            time = '10:00 AM';
            day = 2;
        }
        return {
            time: DateTimeUtils.customFormat(
                time,
                CustomDateFormats.API_TIME,
                DateInputFormats.HOUR12_SHORT
            ),
            day
        };
    };

    cloneBreaks = breaks => {
        const lunch = [];
        const refuel = [];
        const night = [];
        breaks.forEach(operationBreak => {
            switch (operationBreak.breakType) {
                case BreakTypesEnum.Lunch:
                    lunch.push(operationBreak);
                    break;
                case BreakTypesEnum.Refuel:
                    refuel.push(operationBreak);
                    break;
                case BreakTypesEnum.Night:
                    night.push(operationBreak);
                    break;
                default:
                    throw new Error(
                        `break type ${operationBreak.breakType} not found`
                    );
            }
        });
        return {
            lunch,
            refuel,
            night
        };
    };

    updateEndTime = ({ selectedBreak, value }) => {
        const endShiftInstant = selectedBreak.endShiftInstant.setTime(
            DateTimeUtils.customFormat(value, CustomDateFormats.API_TIME)
        );
        return selectedBreak.setEndShiftInstant(endShiftInstant);
    };

    updateStartTime = ({ selectedBreak, value }) => {
        const startShiftInstant = selectedBreak.startShiftInstant
            .setTime(
                DateTimeUtils.customFormat(value, CustomDateFormats.API_TIME)
            )
            .setDay(1);
        return selectedBreak.setStartShiftInstant(startShiftInstant);
    };

    updateLocation = ({ selectedBreak, lat, lng, description }) => {
        return selectedBreak.setLocation(
            AddressLatLng.fromJS({
                address: description,
                latLng: { lat, lng, accuracy: 0 }
            })
        );
    };

    getUpdatedBreakValue = ({
        selectedBreak,
        value,
        lat,
        lng,
        description,
        fieldId
    }) => {
        let newSelectedBreak = selectedBreak;
        switch (fieldId) {
            case 'duration':
                newSelectedBreak = newSelectedBreak.setDuration(
                    value ? parseInt(value) * 60 : 0
                );
                break;
            case 'startTime':
                newSelectedBreak = this.updateStartTime({
                    selectedBreak: newSelectedBreak,
                    value
                });
                break;
            case 'endTime':
                newSelectedBreak = this.updateEndTime({
                    selectedBreak: newSelectedBreak,
                    value
                });
                break;
            case 'location':
                newSelectedBreak = this.updateLocation({
                    selectedBreak: newSelectedBreak,
                    lat,
                    lng,
                    description
                });
                break;
        }
        return newSelectedBreak;
    };

    getUpdatedBreaks = ({ breaks, newBreak, breakType }) => {
        switch (breakType) {
            case BreakTypesEnum.Lunch:
                return {
                    ...breaks,
                    lunch: [...breaks.lunch, newBreak]
                };
            case BreakTypesEnum.Refuel:
                return {
                    ...breaks,
                    refuel: [...breaks.refuel, newBreak]
                };
            case BreakTypesEnum.Night:
                return {
                    ...breaks,
                    night: [...breaks.night, newBreak]
                };
            default:
                throw new Error(`break type ${breakType} not found`);
        }
    };

    getTransactionDuration(breakType: string) {
        switch (breakType) {
            case BreakTypesEnum.Lunch:
                return 1800;
            case BreakTypesEnum.Refuel:
                return 300;
            case BreakTypesEnum.Night:
                return 28800;
            default:
                return 1800;
        }
    }

    getBreakKey({ breakType, breakIndex }) {
        return `${breakType}-${breakIndex}`;
    }

    onAccept = ({ breaks }) => {
        const { breakSlotFor, onChange, closeBreaksDialog } = this.props;
        onChange({
            breaks: _.flatten(_.values(breaks)),
            slotFor: breakSlotFor
        });
        closeBreaksDialog();
    };
    onCancel = (...args) => {
        this.props.closeBreaksDialog(...args);
    };

    render() {
        return (
            <BreaksDialog
                {...this.props}
                showApplyToAll={false}
                closeBreaksDialog={this.onCancel}
                onAcceptBreaks={this.onAccept}
                cloneBreaks={this.cloneBreaks}
                getBreakKey={this.getBreakKey}
                getNewBreak={this.getNewBreak}
                getUpdatedBreaks={this.getUpdatedBreaks}
                getUpdatedBreakValue={this.getUpdatedBreakValue}
            />
        );
    }
}
export default themr(
    'BreakShiftDialog',
    theme
)(translate('configure')(BreakShiftDialog));
